/**
 * Test Harness Types
 *
 * Shared type definitions for the test harness system.
 */

import type { z } from 'zod'
import type {
  ComparisonMode,
  InputParameter,
  MethodSignature,
  OperationRecord,
} from '../../db/schema/problems.js'
import type { TestType } from '../constants/test-types.js'
import type {
  judge0ResultSchema,
  judge0SubmissionSchema,
} from '../schemas/judge0.js'
import type {
  OperationTestResult,
  TestFailure,
  UserFrame,
} from '../types/test-cases.js'

// Re-export for backwards compatibility and so harness consumers get the
// full result contract from one module
export type { OperationTestResult, TestFailure, UserFrame }

// =============================================================================
// Test Configuration Types
// =============================================================================

/**
 * Test configuration as retrieved from the database
 */
export interface TestConfiguration {
  id: string
  problemId: string
  problemSlug?: string | null // Problem slug for auto-generating function names
  testType: TestType
  functionNames?: string[] | null // For function-based tests: acceptable function name slugs (first = preferred, slug-derived always accepted)
  className?: string | null // For class-based tests: the class name to instantiate
  inputParameters: InputParameter[] | null
  methods?: MethodSignature[] | null // For class-based tests: typed method signatures (null = legacy untyped config)
  outputType?: string | null
  comparisonMode?: ComparisonMode | null
  tolerance?: number | null
  approximate?: boolean | null
  caseInsensitive?: boolean | null
}

/**
 * Test case as retrieved from the database
 */
export interface TestCase {
  id: string
  testConfigurationId: string
  inputs?: Record<string, string> | null
  expectedOutput?: string | null
  expectedInputsAfter?: Record<string, unknown> | null
  constructorArgs?: unknown[] | null
  operations?: OperationRecord[] | null
  // For raw test type:
  scripts?: Record<string, string> | null
  exampleDisplay?: string | null
  // Common fields:
  order: number
  intent?: string | null
  inputDisplayOverride?: string | null
  outputDisplayOverride?: string | null
  showAsExample?: boolean | null
}

/**
 * Combined test configuration with test cases
 */
export interface TestConfigWithCases {
  config: TestConfiguration
  cases: TestCase[]
  // Custom test handler fields (populated from database)
  templateCode?: string[] // Template code strings (in order)
  customCode?: CustomCode // Custom setup and verification code
}

// =============================================================================
// Generator Types
// =============================================================================

/**
 * Supported languages for code execution.
 * The const array is the single source for zod enums and iteration; the
 * type derives from it.
 */
export const SUPPORTED_LANGUAGES = ['python', 'javascript', 'java'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

/**
 * Custom code for test harness (setup and verification)
 */
export interface CustomCode {
  setupCode?: string | null // custom_setup() function - transforms inputs
  verificationCode?: string | null // custom_verify() function - validates outputs
}

/**
 * Input to the test program generator
 */
export interface GeneratorInput {
  userCode: string
  testConfig: TestConfiguration
  testCases: TestCase[]
  language: SupportedLanguage
  // Custom test handler fields (optional)
  templateCode?: string[] // Array of template code strings (in order)
  customCode?: CustomCode // Custom setup and verification code
  /**
   * Bake STOP_ON_FAILURE into the generated program.
   * Submit passes true (halt at first failing case); Run passes false
   * (all example cases execute). Defaults to false.
   */
  stopOnFailure?: boolean
}

/**
 * Output from the test program generator
 */
export interface GeneratedProgram {
  sourceCode: string
  languageId: number // Judge0 language ID (113 for Python 3.14, 102 for Node.js 22)
  /**
   * Extra files shipped beside the driver via a Judge0 additional_files
   * zip: e.g. { 'solution.py': ..., 'defs.py'?: ... } (Python) or
   * { 'solution.js': ..., 'defs.js'?: ... } (JavaScript). Absent =
   * single-file program (raw mode).
   */
  additionalFiles?: Record<string, string>
  /**
   * Number of preamble lines prepended to solution.py (0 when the problem
   * has no template classes). Editor line = solution.py line − this value.
   * Recorded so remapping never guesses.
   */
  solutionPreambleLines?: number
}

// =============================================================================
// Result Types
// =============================================================================

/**
 * Result of a single test case execution
 */
export interface TestResult {
  index: number
  passed: boolean
  expected?: unknown
  actual?: unknown
  /** @deprecated legacy string error — read `failure` instead (removed in the LG-165 UI plan) */
  error?: string
  /** @deprecated legacy error class name — read `failure.errorType` instead */
  errorType?: string
  /** Structured failure payload (LG-165) — present only on failing results */
  failure?: TestFailure
  operations?: OperationTestResult[]
  // Comparison inputs + authoring context. Emitted by the FUNCTION runners on
  // every executed result (passing included) so the Run view can render full
  // per-case tabs; absent on legacy stored results, on cases skipped by
  // stop-early, and on class cases, which carry `operations` instead.
  // Presence rules live with the `Wire*` payload types.
  inputs?: Record<string, unknown>
  /**
   * Post-call state of every input param, serialized. Emitted by the FUNCTION
   * runners on a FAILING custom-verify case (LG-561): those verifiers return a
   * bare boolean, so one that rejects on post-call state — the array was never
   * mutated in place — otherwise renders an identical expected/actual with no
   * reason. A passing case has nothing to explain, so it carries none.
   *
   * The CLASS runners never emit it: there custom-verify receives the object
   * under test rather than the input map. Dumping instance state was considered
   * and rejected (LG-669, 2026-08-13 — "what is the state of an arbitrary user
   * object" has no clean general answer); the class-side evidence is the
   * operation's raw return value instead, declared as
   * `WIRE_KEYS.operationResult.rawReturn`.
   */
  inputsAfter?: Record<string, unknown>
  intent?: string
  inputDisplayOverride?: string
  outputDisplayOverride?: string
  // Raw test type fields
  script?: string
}

/**
 * Parsed results from test execution output
 */
export interface ParsedResults {
  success: boolean
  results: TestResult[]
  summary: {
    total: number
    passed: number
    failed: number
  }
  compilationError?: string
  runtimeError?: string
  /**
   * Program-level failure reported through the results protocol (user
   * syntax error or import-time crash). compilationError/runtimeError are
   * still populated from it for legacy consumers.
   */
  programFailure?: TestFailure
}

// =============================================================================
// Judge0 Types
// =============================================================================

/**
 * Options for Judge0 submission
 */
export interface Judge0SubmitOptions {
  sourceCode: string
  languageId: number
  cpuTimeLimit?: number // seconds
  wallTimeLimit?: number // seconds
  memoryLimit?: number // KB
  wait?: boolean // true for sync, false for async
  callbackUrl?: string // Judge0 callback URL for async results
  /** { path: content } map zipped in-process and sent base64 as additional_files */
  additionalFiles?: Record<string, string>
  signal?: AbortSignal // caller abort (request disconnect); combined with the client's own timeout
}

/** Judge0 submission response (async mode) — derived from the runtime schema. */
export type Judge0Submission = z.infer<typeof judge0SubmissionSchema>

/** Judge0 result (status codes: 1=in queue, 2=processing, 3+=completed). */
export type Judge0Result = z.infer<typeof judge0ResultSchema>

/**
 * Decoded Judge0 output (after base64 decoding)
 */
export interface DecodedJudge0Output {
  stdout: string | null
  stderr: string | null
  compileOutput: string | null
}

// =============================================================================
// Language IDs
// =============================================================================

/**
 * Judge0 language IDs for supported languages
 */
export const LANGUAGE_IDS: Record<SupportedLanguage, number> = {
  python: 113, // Python 3.14.0
  javascript: 102, // JavaScript (Node.js 22.08.0)
  java: 91, // Java (JDK 17.0.6)
}
